import { useQuery } from '@tanstack/react-query';

import { mapFacilityIdToApiParam, searchOosLogEvents } from 'api/warehouse/oosLog';
import { ApiError } from 'shared/errors/ApiError';

export const OOS_LOG_EVENTS_QUERY_KEY = 'oosLogEvents';

export const oosLogEventsQueryKeys = {
    all: [OOS_LOG_EVENTS_QUERY_KEY],
    list: (facilityId, filters, page, pageSize) => [OOS_LOG_EVENTS_QUERY_KEY, facilityId, filters, page, pageSize],
};

/**
 * Avoid showing another facility's events while a facility switch is in flight.
 *
 * @param {number | undefined} nextFacilityId
 * @returns {(previousData: unknown, previousQuery: { queryKey?: unknown[] }) => unknown}
 */
export function createOosLogEventsPlaceholderData(nextFacilityId) {
    return (previousData, previousQuery) => {
        const previousFacilityId = previousQuery?.queryKey?.[1];
        if (previousFacilityId !== nextFacilityId) {
            return undefined;
        }
        return previousData;
    };
}

/**
 * Fetches paginated OOS log events for the current facility.
 *
 * @param {Object} options
 * @param {number|string|null|undefined} options.facilityId
 * @param {number} [options.page=1]
 * @param {number} [options.pageSize=50]
 * @param {number} [options.externalSku]
 * @param {number[]} [options.companySourceIds]
 * @param {number} [options.packLineNumber]
 * @param {boolean} [options.noPackLine]
 * @param {string} [options.authUser]
 * @param {string} [options.start]
 * @param {string} [options.end]
 * @param {boolean} [options.enabled=true]
 */
export function useOosLogEventsQuery({
    authUser,
    companySourceIds,
    enabled = true,
    end,
    externalSku,
    facilityId,
    noPackLine,
    packLineNumber,
    page = 1,
    pageSize = 50,
    start,
} = {}) {
    const normalizedFacilityId = mapFacilityIdToApiParam(facilityId);
    const filters = {
        authUser,
        companySourceIds,
        end,
        externalSku,
        noPackLine,
        packLineNumber,
        start,
    };

    return useQuery({
        enabled: Boolean(enabled && normalizedFacilityId),
        queryKey: oosLogEventsQueryKeys.list(normalizedFacilityId, filters, page, pageSize),
        retry: false,
        placeholderData: createOosLogEventsPlaceholderData(normalizedFacilityId),
        queryFn: async ({ signal }) => {
            const response = await searchOosLogEvents(
                {
                    facilityId: normalizedFacilityId,
                    page,
                    pageSize,
                    externalSku,
                    companySourceIds,
                    packLineNumber,
                    noPackLine,
                    authUser,
                    start,
                    end,
                },
                { signal },
            );

            if (!response?.status) {
                throw new ApiError(
                    response?.msg ?? 'Failed to fetch OOS log events',
                    response?.statusCode,
                    response?.data,
                );
            }

            return {
                events: response.data?.events ?? [],
                pagination: {
                    page: response.data?.pagination?.page ?? page,
                    pageSize: response.data?.pagination?.pageSize ?? pageSize,
                    total: response.data?.pagination?.total ?? 0,
                },
            };
        },
    });
}
